<script setup lang="ts">
import { PasswordRequirements, VButton, VLoadingSection, VPasswordField, VTextField } from '@/modules/shared/components'
import InputCheckbox from '@/modules/shared/components/InputCheckbox.vue'
import useVuelidate from '@vuelidate/core'
import { helpers, required, sameAs } from '@vuelidate/validators'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/modules/public/stores/user-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useWorkspaceStore } from '../../stores/workspace-store'
import AuthLayout from '@/modules/auth/layouts/auth-layout.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import FormError from '@/modules/auth/components/form-error.vue'

const route = useRoute()
const router = useRouter()
const { slug } = route.params as { slug: string }

const userStore = useUserStore()
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()
const workspace = computed(() => workspaceStore.workspace)
const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const requiredTrue = helpers.withMessage('This field must be checked.', (value: boolean) => value === true)

const loading = ref(false)
const passwordRequirementsValid = ref(false)
const passwordSubmitAttempted = ref(false)
const form = ref({
  name: null,
  password: null,
  password_confirmation: null,
  is_agreement_accepted: false,
})
const rules = {
  name: { required },
  password: { required },
  password_confirmation: { required, sameAsRepeat: sameAs(computed(() => form.value.password)) },
  is_agreement_accepted: { requiredTrue },
}
const v$ = useVuelidate(rules, form, { $lazy: true })

const submitForm = async () => {
  passwordSubmitAttempted.value = true
  const valid = await v$.value.$validate()
  if (!valid || !passwordRequirementsValid.value) return

  loading.value = true
  userStore.clearErrorMessage()
  await userStore.createCompanyUser(form.value, { slug })
  loading.value = false
}

onMounted(async () => {
  showSkeleton()
  await authStore.refresh(null, route)
  await workspaceStore.fetchWorkspace(slug)

  if (!workspace.value.options.is_funding_application_enabled) router.push({ name: 'public.welcome' })
  hideSkeleton()
})
</script>

<template>
  <AuthLayout>
    <template v-slot:icon>
      <svg width="72" height="96" viewBox="0 0 72 96" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M63 39H9C5.68629 39 3 41.6863 3 45V87C3 90.3137 5.68629 93 9 93H63C66.3137 93 69 90.3137 69 87V45C69 41.6863 66.3137 39 63 39Z"
          fill="#111827"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M15 39V24C15 12.4 24.4 3 36 3C47.56 3 57 12.4 57 24V39"
          stroke="#111827"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M36 60V72" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>

    <div class="mx-32 w-full" v-if="skeleton">
      <VLoadingSection
        title="Verifying Token"
        description="Please wait a moment while we set up your company account. Your data is being processed to ensure security of your account."
      />
    </div>
    <div class="mx-32 w-full" v-else>
      <h2 class="mb-7 text-2xl font-bold leading-5 text-gray-900 dark:text-gray-300 sm:text-3xl sm:leading-7">
        Create Company Account
      </h2>
      <div class="mb-10">Please complete the information below to set-up your account.</div>
      <FormError :error="userStore.errorMessage" v-show="userStore.errorMessage !== null" />
      <form @submit.prevent="submitForm" class="mb-12">
        <div class="space-y-5">
          <VTextField v-model="form.name" label="Your Name" placeholder="Name" property="name" :v$="v$" />
          <div>
            <VPasswordField
              v-model="form.password"
              label="Password"
              placeholder="Password"
              property="password"
              :v$="v$"
            />
            <PasswordRequirements
              :password="form.password"
              :show-errors="passwordSubmitAttempted"
              @update:valid="passwordRequirementsValid = $event"
            />
          </div>
          <VPasswordField
            v-model="form.password_confirmation"
            label="Confirm Password"
            placeholder="Confirm Password"
            property="password_confirmation"
            :v$="v$"
          />
        </div>

        <label class="mt-6 flex cursor-pointer select-none items-center gap-3 text-sm">
          <InputCheckbox v-model="form.is_agreement_accepted" />
          <span>
            I agree to the
            <a href="https://www.venture360.co/terms" target="_blank" class="hyperlink">Terms of Service ↗</a>
            and
            <a href="https://www.venture360.co/privacy" target="_blank" class="hyperlink">Privacy Policy ↗</a>
          </span>
        </label>
      </form>
      <VButton
        :click="submitForm"
        :loading="loading"
        class="w-full !bg-[#3D8A9D] font-semibold"
        variant="primary"
        size="xl"
        :disabled="!form.is_agreement_accepted"
      >
        Register
      </VButton>

      <div class="mt-10 text-center text-sm">
        Already have an account?
        <RouterLink :to="{ name: 'login', query: { account: slug } }" class="ml-1 font-semibold text-[#3D8A9D]">
          Login
        </RouterLink>
      </div>
    </div>
  </AuthLayout>
</template>
